import { LessonStep } from "../types/v2";

export const activityPresets = [
  ["explanation", "Explanation / presentation", "Teach without a submission"],
  ["mcq", "Multiple-choice", "Question, choices and correct answer"],
  ["matching", "Matching pairs", "Connect terms and meanings"],
  ["ordering", "Ordering", "Arrange items in a sequence"],
  [
    "drag_drop_fill_blanks",
    "Drag-and-drop blanks",
    "Place tokens into a template",
  ],
  ["text", "Written response", "A short student answer"],
  ["demonstration", "Demonstration", "Show a process or worked example"],
  ["simulation", "Simulation", "Use a supported server template"],
  ["video", "Video", "Video with optional supporting text"],
  ["slides", "Presentation slides", "Reuse a linked Drive presentation"],
] as const;
export type ActivityPreset = (typeof activityPresets)[number][0];
export function activityPresetPatch(type: ActivityPreset): Partial<LessonStep> {
  const responding = [
    "mcq",
    "matching",
    "ordering",
    "drag_drop_fill_blanks",
    "text",
  ].includes(type);
  const source =
    type === "matching"
      ? { type, question: "", pairs: {} }
      : type === "ordering"
        ? { type, question: "", items: [], answer: [] }
        : type === "drag_drop_fill_blanks"
          ? { type, question: "", target: "", tokens: [], answer_code: "" }
          : null;
  return {
    title: activityPresets.find((p) => p[0] === type)![1],
    step_kind: responding || type === "simulation" ? "practice" : "instruction",
    completion_role: responding || type === "simulation" ? "check" : "optional",
    live_response_mode: responding
      ? type === "text"
        ? "guided_response"
        : "quick_check"
      : "none",
    content: {
      authoring_format: type,
      blocks:
        type === "video"
          ? [{ type: "video", src: "", caption: "", transcript: "" }]
          : [{ type: "text", text: "" }],
      ...(source ? { source_item: source } : {}),
    },
    signal_config: responding
      ? {
          type,
          prompt: "",
          confidence_required: false,
          reason_required: false,
          reasoning_gate: { mode: "off" },
          ...(type === "mcq" ? { options: ["", ""] } : {}),
        }
      : {},
  };
}
export function activityLabel(
  step: Pick<
    LessonStep,
    "content" | "signal_config" | "activity_format" | "live_response_mode"
  >,
): string {
  if (step.activity_format === "simulation") return "Simulation";
  const source =
    step.content?.source_item ||
    step.content?.sourceItem ||
    step.signal_config?.source_item;
  const type =
    source?.type ||
    step.signal_config?.type ||
    step.signal_config?.signal_type ||
    step.content?.authoring_format;
  return (
    activityPresets.find((p) => p[0] === type)?.[1] ||
    (step.live_response_mode && step.live_response_mode !== "none"
      ? "Response activity"
      : "Explanation / presentation")
  );
}
